import { capitalize } from '../formatting/formattingService';
import { E_COMMERCE_CATEGORY, TrackHeaderActions, SEARCH_CATEGORY, SEARCH_LOGS_CATEGORY } from './types';
import { DataLayerManagerInterface } from './dataLayerManager/abstractDataLayerManager';

export enum SuggestionType {
    Categories = 'Categories',
    Brands = 'Brands',
    Products = 'Products',
    StaticPages = 'Static_pages_navigation_title',
}

export enum RedirectMethod {
    Empty = '',
    Manual = 'manual',
    Automatic = 'automatic',
}

export enum SearchSubmissionMethod {
    Empty = '',
    DirectInput = 'DirectInput',
    Suggestion = 'Suggestion',
    SuggestionCategory = 'Suggestion.Category',
    SuggestionBrand = 'Suggestion.Brand',
    SuggestionProduct = 'Suggestion.Product',
    SuggestionInformation = 'Suggestion.Information',
}

export enum SearchMethod {
    Empty = '',
    RedirectManual = 'Redirect.Manual',
    RedirectAutomatic = 'Redirect.Automatic',
    Strict = 'AllProductsSearch.Strict',
    Fuzzy = 'AllProductsSearch.Fuzzy',
}

export interface SearchData {
    searchTerm: string;
    productsCount: number;
    searchMethod: SearchMethod;
    submissionMethod: SearchSubmissionMethod;
    redirectPage: string;
    isRealSearch: boolean;
}

export interface SearchTrackingData {
    /* eslint-disable @typescript-eslint/camelcase, camelcase */
    real_search: '0' | '1';
    search_submission_method: SearchSubmissionMethod;
    search_results: string;
    search_term: string;
    search_method: SearchMethod;
    redirect_type: string;
    /* eslint-enable @typescript-eslint/camelcase, camelcase */
}

export const defaultSearchGaData: SearchTrackingData = {
    real_search: '0',
    search_submission_method: SearchSubmissionMethod.Empty,
    search_results: '-1',
    search_term: '',
    search_method: SearchMethod.Empty,
    redirect_type: '',
};

export const getSearchSubmissionMethod = (suggestionType: string): SearchSubmissionMethod => {
    const submissionMethod = {
        [SuggestionType.Categories]: SearchSubmissionMethod.SuggestionCategory,
        [SuggestionType.Brands]: SearchSubmissionMethod.SuggestionBrand,
        [SuggestionType.Products]: SearchSubmissionMethod.SuggestionProduct,
        [SuggestionType.StaticPages]: SearchSubmissionMethod.SuggestionInformation,
    };

    return submissionMethod[suggestionType] || SearchSubmissionMethod.DirectInput;
};

export const getDynamicSearchSubmissionMethod = (clickedSuggestion: string): SearchSubmissionMethod => {
    return clickedSuggestion ? SearchSubmissionMethod.Suggestion : SearchSubmissionMethod.DirectInput;
};

export const mapToSearchTrackingData = (searchData: SearchData): SearchTrackingData => {
    return {
        real_search: searchData.isRealSearch ? '1' : '0',
        search_submission_method: searchData.submissionMethod,
        search_results: searchData.productsCount.toString(),
        search_term: searchData.searchTerm,
        search_method: searchData.searchMethod,
        redirect_type: capitalize(searchData.redirectPage),
    };
};

export default class DynamicSearchTracker {
    private dataLayerManager: DataLayerManagerInterface;

    public constructor(dataLayerManager: DataLayerManagerInterface) {
        this.dataLayerManager = dataLayerManager;
    }

    public trackInputClick = () => {
        this.dataLayerManager.sendGenericEvent({
            category: 'Header',
            action: 'Search overlay clicks',
            label: 'Search bar click',
        });
    };

    public trackSearchPerformed(searchValue: string) {
        this.dataLayerManager.sendGenericEvent({
            category: SEARCH_LOGS_CATEGORY,
            action: 'Search performed',
            label: searchValue,
        });
    }

    public trackSearchUrlParam(searchValue: string) {
        this.dataLayerManager.sendGenericEvent({
            category: E_COMMERCE_CATEGORY,
            action: 'PLP-URL-SearchTerm',
            label: (searchValue || '').toLocaleLowerCase(),
        });
    }

    public trackSearchWithNoResult(searchValue?: string) {
        this.dataLayerManager.sendGenericEvent({
            category: SEARCH_CATEGORY,
            action: 'No result page',
            label: (searchValue || '').toLocaleLowerCase(),
        });
    }

    public trackMobileIconClick = () => {
        this.dataLayerManager.sendSpecificEvent('searchiconclick', {
            category: 'Header',
            action: TrackHeaderActions.HeaderSearchIconClick,
        });
    };

    public trackOverlayCloseClick = () => {
        this.dataLayerManager.sendGenericEvent({
            category: 'Header',
            action: 'Search overlay clicks',
            label: 'dismiss search overlay',
        });
    };

    public trackSuggestionPosition(searchValue: string, suggestionIndex: number) {
        this.dataLayerManager.sendGenericEvent({
            category: 'SearchSuggestionPosition',
            action: (suggestionIndex + 1).toString(),
            label: (searchValue || '').toLocaleLowerCase(),
        });
    }
}
